import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { SectionWithHeading } from '../../../../shared/components/common/sectionWithHeading';
import { DirectDebitService } from '../../../../services/directDebitService';
import { useTranslation } from 'react-i18next';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DirectDebitDdaStatus } from '../../../../constants/directDebitEnums';
import { mapDdaStatusFromApiCode } from '../../../../helpers/directDebitStatusHelpers';

const STATUS_PANEL = {
	[DirectDebitDdaStatus.Active]: {
		bg: '#E8F5E9',
		titleColor: '#2E7D32',
		subtitleColor: 'rgba(0, 0, 0, 0.71)',
		icon: (props) => <CheckCircleRoundedIcon sx={{ fontSize: 40, color: '#2E7D32' }} {...props} />,
	},
	pending: {
		bg: '#FFF8E1',
		titleColor: '#E65100',
		subtitleColor: 'rgba(0, 0, 0, 0.71)',
		icon: (props) => <PendingOutlinedIcon sx={{ fontSize: 40, color: '#E65100' }} {...props} />,
	},
	negative: {
		bg: '#FFEBEE',
		titleColor: '#C62828',
		subtitleColor: 'rgba(0, 0, 0, 0.71)',
		icon: (props) => <CancelOutlinedIcon sx={{ fontSize: 40, color: '#C62828' }} {...props} />,
	},
	neutral: {
		bg: '#F5F5F5',
		titleColor: '#424242',
		subtitleColor: 'rgba(0, 0, 0, 0.71)',
		icon: (props) => <InfoOutlinedIcon sx={{ fontSize: 40, color: '#616161' }} {...props} />,
	},
};

const pendingStatuses = new Set([
	DirectDebitDdaStatus.Pending,
	DirectDebitDdaStatus.PendingBankSubmission,
	DirectDebitDdaStatus.PendingBankApproval,
	DirectDebitDdaStatus.Suspended,
]);

const negativeStatuses = new Set([
	DirectDebitDdaStatus.Rejected,
	DirectDebitDdaStatus.Denied,
	DirectDebitDdaStatus.Error,
]);

function parseStatusDescription(description, status, t) {
	const fallbackTitle = (() => {
		if (status === DirectDebitDdaStatus.Active) return t('active');
		if (pendingStatuses.has(status)) return t('pendingAuthorization');
		return '';
	})();

	if (!description?.trim()) {
		return { title: fallbackTitle, subtitle: '' };
	}

	const sep = ' - ';
	if (description.includes(sep)) {
		const i = description.indexOf(sep);
		return {
			title: description.slice(0, i).trim(),
			subtitle: description.slice(i + sep.length).trim(),
		};
	}

	return {
		title: fallbackTitle || description,
		subtitle: fallbackTitle ? description : '',
	};
}

function getPanelForStatus(status) {
	if (status === DirectDebitDdaStatus.Active) return STATUS_PANEL[DirectDebitDdaStatus.Active];
	if (pendingStatuses.has(status)) return STATUS_PANEL.pending;
	if (negativeStatuses.has(status)) return STATUS_PANEL.negative;
	return STATUS_PANEL.neutral;
}

export const DirectDebitStatusCard = ({ loanId }) => {
	const directDebitService = new DirectDebitService();
	const { t } = useTranslation();
	const [mandate, setMandate] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (loanId) {
			loadMandate();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loanId]);

	const loadMandate = () => {
		setLoading(true);
		directDebitService
			.getMandateByLoanId(loanId)
			.then((resp) => {
				setMandate(resp.data);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const banner = useMemo(() => {
		if (!mandate) {
			const panel = STATUS_PANEL.neutral;
			return {
				panel,
				title: t('noMandate'),
				subtitle: t('noDirectDebit') || 'No direct debit mandate set up for this loan',
			};
		}

		const status = mapDdaStatusFromApiCode(mandate.status);
		const panel = getPanelForStatus(status);
		const { title, subtitle } = parseStatusDescription(mandate.statusDescription, status, t);

		let resolvedTitle = title;
		if (!resolvedTitle) {
			if (status === DirectDebitDdaStatus.Active) resolvedTitle = t('active');
			else if (pendingStatuses.has(status)) resolvedTitle = t('pendingAuthorization');
			else resolvedTitle = mandate.statusDescription || mandate.status || '';
		}

		return { panel, title: resolvedTitle, subtitle: subtitle };
	}, [mandate, t]);

	return (
		<SectionWithHeading
			heading={t('directDebitStatus')}
			loading={loading}
			bodyClass="!py-3"
			classList="!shadow-sm md:!shadow-md !border !border-gray-200"
			children={
				<Box className="flex flex-col w-full h-auto min-h-0">
					<Box
						sx={{
							bgcolor: banner.panel.bg,
							borderRadius: 2,
							px: 2.5,
							py: 2,
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'flex-start',
							gap: 2,
							width: '100%',
							boxSizing: 'border-box',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
							{banner.panel.icon()}
						</Box>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								component="div"
								sx={{
									fontWeight: 700,
									color: banner.panel.titleColor,
									fontSize: '1rem',
									lineHeight: 1.35,
								}}
							>
								{banner.title}
							</Typography>
							{banner.subtitle ? (
								<Typography
									component="div"
									variant="body2"
									sx={{
										color: banner.panel.subtitleColor,
										mt: 0.75,
										lineHeight: 1.5,
									}}
								>
									{banner.subtitle}
								</Typography>
							) : null}
						</Box>
					</Box>
				</Box>
			}
		/>
	);
};
